import React, { useRef } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { useTheme } from "../../theme/ThemeContext";
import { PAPER_MODES } from "../../theme/themePresets";
import { useFontSize } from "../../theme/FontSizeContext";
import {
  READER_FONT_FAMILIES,
  READER_LINE_HEIGHTS,
  READER_WIDTH_PRESETS,
  useReaderTypography,
} from "../../theme/ReaderTypographyContext";

const QuickSettingsContent = ({
  isEn,
  onClose,
  onOpenMoreSettings,
}) => {
  const { paperMode, contentWidth, setPaperMode, setContentWidth } = useTheme();
  const { fontSize, changeFontSize } = useFontSize();
  const { fontFamilyId, lineHeight, setFontFamilyId, setLineHeight } =
    useReaderTypography();
  const lastLightModeRef = useRef("sepia");

  const currentPaper = PAPER_MODES.find((m) => m.id === paperMode);
  const isNight = Boolean(currentPaper?.isDark);

  const handleToggleNight = (checked) => {
    if (checked) {
      if (!isNight && paperMode) {
        lastLightModeRef.current = paperMode;
      }
      setPaperMode("dark");
    } else {
      setPaperMode(lastLightModeRef.current || "sepia");
    }
  };

  const clampFontSize = (next) => Math.min(40, Math.max(12, next));

  return (
    <Box sx={{ p: 2.25, width: { xs: "100%", sm: 340 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {isEn ? "Quick Reading Settings" : "快捷阅读设置"}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={isEn ? "Close" : "关闭"}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* 1. Theme circles + Night toggle */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            {isEn ? "Theme" : "阅读主题"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <DarkModeOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {isEn ? "Night" : "夜间"}
            </Typography>
            <Switch
              size="small"
              checked={Boolean(isNight)}
              onChange={(e) => handleToggleNight(e.target.checked)}
              inputProps={{ "aria-label": isEn ? "Night mode" : "夜间模式" }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
          }}
        >
          {PAPER_MODES.map((mode) => {
            const selected = paperMode === mode.id;
            return (
              <Tooltip
                key={mode.id}
                title={isEn ? mode.nameEn : mode.nameZh}
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!mode.isDark) {
                      lastLightModeRef.current = mode.id;
                    }
                    setPaperMode(mode.id);
                  }}
                  aria-label={isEn ? mode.nameEn : mode.nameZh}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: mode.bgPaper,
                    color: mode.textPrimary,
                    border: "2px solid",
                    borderColor: selected ? "primary.main" : mode.divider,
                    "&:hover": {
                      bgcolor: mode.bgPaper,
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  {selected ? <CheckIcon sx={{ fontSize: 15 }} /> : null}
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* 2. Font family */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 700, color: "text.secondary", mb: 1 }}
        >
          {isEn ? "Font Family" : "正文字体"}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
          {READER_FONT_FAMILIES.map((font) => {
            const selected = fontFamilyId === font.id;
            return (
              <ButtonBase
                key={font.id}
                onClick={() => setFontFamilyId(font.id)}
                aria-pressed={selected}
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  border: "1.5px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  bgcolor: selected ? "action.selected" : "transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: selected ? 800 : 600,
                    fontFamily: font.css,
                    color: selected ? "primary.main" : "text.primary",
                  }}
                >
                  {isEn ? font.nameEn : font.nameZh}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    fontFamily: font.css,
                    color: "text.secondary",
                    mt: 0.2,
                  }}
                >
                  {isEn ? font.previewEn : font.previewZh}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* 3. Font size A- / A+ */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 700, color: "text.secondary", mb: 1 }}
        >
          {isEn ? "Font Size" : "字号大小"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => changeFontSize(clampFontSize(fontSize - 2))}
            disabled={fontSize <= 12}
            aria-label="A-"
            sx={{ flex: 1, fontWeight: 700 }}
          >
            A-
          </Button>
          <Typography
            sx={{
              minWidth: 56,
              textAlign: "center",
              fontWeight: 800,
              fontSize: "0.9rem",
            }}
          >
            {fontSize}px
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => changeFontSize(clampFontSize(fontSize + 2))}
            disabled={fontSize >= 40}
            aria-label="A+"
            sx={{ flex: 1, fontWeight: 700 }}
          >
            A+
          </Button>
        </Box>
      </Box>

      {/* 4. Line height */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 700, color: "text.secondary", mb: 1 }}
        >
          {isEn ? "Line Height" : "行距"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {READER_LINE_HEIGHTS.map((item) => {
            const selected = Math.abs(lineHeight - item.value) < 0.05;
            return (
              <Button
                key={item.id}
                size="small"
                variant={selected ? "contained" : "outlined"}
                onClick={() => setLineHeight(item.value)}
                sx={{
                  flex: 1,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: 1.5,
                }}
              >
                {isEn ? item.nameEn : item.nameZh}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* 5. Page width quick presets */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 700, color: "text.secondary", mb: 1 }}
        >
          {isEn ? "Page Width" : "页面宽度"} ({contentWidth}%)
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {READER_WIDTH_PRESETS.map((preset) => {
            const selected = contentWidth === preset.value;
            return (
              <Button
                key={preset.id}
                size="small"
                variant={selected ? "contained" : "outlined"}
                onClick={() => setContentWidth(preset.value)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: 0.75,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: 1.5,
                }}
              >
                {isEn ? preset.nameEn : preset.shortZh}
              </Button>
            );
          })}
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* 6. More settings */}
      <Button
        fullWidth
        size="small"
        variant="text"
        startIcon={<TuneIcon fontSize="small" />}
        onClick={() => {
          if (onClose) onClose();
          if (onOpenMoreSettings) onOpenMoreSettings();
        }}
        sx={{
          fontWeight: 700,
          textTransform: "none",
        }}
      >
        {isEn ? "More Settings..." : "更多设置"}
      </Button>
    </Box>
  );
};

const QuickSettingsPopover = ({
  open,
  anchorEl,
  onClose,
  onOpenMoreSettings,
  edition,
  isMobile = false,
}) => {
  const isEn = edition?.id === "en";

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: "env(safe-area-inset-bottom)",
          },
        }}
      >
        <QuickSettingsContent
          isEn={isEn}
          onClose={onClose}
          onOpenMoreSettings={onOpenMoreSettings}
        />
      </Drawer>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          ml: 1,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
        },
      }}
    >
      <QuickSettingsContent
        isEn={isEn}
        onClose={onClose}
        onOpenMoreSettings={onOpenMoreSettings}
      />
    </Popover>
  );
};

export default QuickSettingsPopover;
